import type { Dayjs } from 'dayjs';
import type { ComponentProps } from 'react';
import type { Control, FieldPath, FieldValues } from 'react-hook-form';

import { DatePicker } from '@/components/common/date-picker';
import { fromDate, toDayjs } from '@/lib/date';

import { FormField } from '../form-field';

type DateBaseProps = Omit<ComponentProps<typeof DatePicker>, 'value' | 'onChange'>;

export type DateFieldProps<
  TFieldValues extends FieldValues,
  TName extends FieldPath<TFieldValues>,
> = DateBaseProps & {
  name: TName;
  control: Control<TFieldValues>;
  label?: string;
  help?: string;
  required?: boolean;
};

export function DateField<TFieldValues extends FieldValues, TName extends FieldPath<TFieldValues>>({
  name,
  control,
  label,
  help,
  required,
  ...rest
}: DateFieldProps<TFieldValues, TName>) {
  return (
    <FormField name={name} control={control} label={label} help={help} required={required}>
      {({ field, fieldState }) => (
        <DatePicker
          {...rest}
          id={name}
          style={{ width: '100%', ...rest.style }}
          status={fieldState.error ? 'error' : undefined}
          value={toDayjs(field.value)}
          onChange={(date) => field.onChange(fromDate(date as Dayjs | null))}
          onBlur={field.onBlur}
          ref={field.ref}
        />
      )}
    </FormField>
  );
}
